<script setup lang="ts">
import { computed } from "vue";

export type TcTooltipProps = {
  text: string;
  isVisible: boolean;
  activation?: "hover" | "click" | "both";
  position?: "top" | "bottom" | "left" | "right" | "top-left" | "top-right" | "bottom-left" | "bottom-right";
};

const props = withDefaults(defineProps<TcTooltipProps>(), {
  isVisible: false,
  position: "top-right",
  activation: "both",
});

const tooltipClasses = computed(() => ({
  "tc-tooltip__text": true,
  [`tc-tooltip--${props.position}`]: true,
  "tc-tooltip__text--clicked": ["click", "both"].includes(props.activation) && props.isVisible,
}));

const arrowClass = computed(() => ({
  "tc-tooltip__arrow": true,
  [`tc-tooltip__arrow--${props.position}`]: true,
}));
</script>

<template>
  <div
    class="tc-tooltip__wrapper"
    :class="{ 'tc-tooltip__wrapper--hovered': ['both', 'hover'].includes(activation) }"
  >
    <slot />

    <div v-show="isVisible" class="tc-tooltip">
      <span role="tooltip" :class="tooltipClasses">{{ text }} <span :class="arrowClass" /></span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tc-tooltip {
  &__wrapper {
    position: relative;
    display: inline-block;

    &--hovered:hover .tc-tooltip__text {
      visibility: visible;
      opacity: 1;
    }
  }

  &__text {
    @include colors.color(content, inverse);
    @include sp.padding(false, 2);
    @include colors.bg(background, overlay);
    @include text.modification(center);

    border-radius: $space-1;
    visibility: hidden;
    opacity: 0;
    transition: opacity 1s;
    min-width: 125px;
    max-width: 102px;
    position: absolute;
    z-index: 1000;

    &--clicked {
      visibility: visible;
      opacity: 1;
    }
  }

  &--top {
    right: 50%;
    bottom: 100%;
  }

  &--top-left {
    left: 0;
    bottom: 100%;
  }

  &--top-right {
    right: 0;
    bottom: 100%;
  }

  &--bottom {
    right: 50%;
    top: 100%;
  }

  &--bottom-left {
    left: 0;
    top: 100%;
  }

  &--bottom-right {
    right: 0;
    top: 100%;
  }

  &--left {
    top: 50%;
    left: 0;
  }

  &--right {
    top: 50%;
    right: 0;
  }

  &__arrow {
    border-width: $space-2;
    content: " ";
    position: absolute;
    border-style: solid;

    &--top {
      left: 50%;
      top: 100%;

      border-color: $color-neutral-pre-mid transparent transparent transparent;
    }

    &--top-left {
      left: 10%;
      top: 100%;

      border-color: $color-neutral-pre-mid transparent transparent transparent;
    }

    &--top-right {
      right: 10%;
      top: 100%;

      border-color: $color-neutral-pre-mid transparent transparent transparent;
    }

    &--bottom {
      right: 50%;
      bottom: 100%;

      border-color: transparent transparent $color-neutral-pre-mid transparent;
    }

    &--bottom-left {
      left: 10%;
      bottom: 100%;

      border-color: transparent transparent $color-neutral-pre-mid transparent;
    }

    &--bottom-right {
      right: 10%;
      bottom: 100%;

      border-color: transparent transparent $color-neutral-pre-mid transparent;
    }

    &--left {
      top: 50%;
      right: 100%;

      border-color: transparent transparent transparent $color-neutral-pre-mid;
    }

    &--right {
      top: 50%;
      left: 100%;

      border-color: transparent $color-neutral-pre-mid transparent transparent;
    }
  }
}

:slotted(*):focus + .tc-tooltip__text {
  visibility: visible;
  opacity: 1;
}
</style>
